import { Component, OnInit } from '@angular/core';
import { Router, NavigationEnd, RouterModule } from '@angular/router';
import { filter } from 'rxjs/operators';
import { AuthService } from './core/services/auth.service';
import { CacheManagerService } from './core/services/cache-manager.service';
import { CommonModule } from '@angular/common';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],   
    standalone: true,
    imports: [RouterModule, CommonModule]
})
export class AppComponent implements OnInit {
  title = 'UI';
  
  private retryCount = 0;
  private maxRetries = 3;
  
  public isInitializing = true;
  
  constructor(
    public authService: AuthService,
    private router: Router,
    private cacheManagerService: CacheManagerService
  ) {}

  async ngOnInit() {
    try {
      // Initialize MSAL
      await this.authService.ensureMsalInitialized();
      this.isInitializing = false;
      
      // Listen for route changes
      this.router.events.pipe(
        filter(event => event instanceof NavigationEnd)
      ).subscribe((event: NavigationEnd) => {
        // If we're on a protected route and not authenticated, try to use stored account
        if (!event.url.includes('/auth/') && !this.authService.isAuthenticated()) {
          if (!this.authService.tryUseStoredAccount()) {
            // If we can't authenticate silently, redirect to login
            this.router.navigate(['/auth/login']);
          }
        }
      });
      
      // Try to use stored account on app initialization
      if (!this.authService.isAuthenticated()) {
        this.authService.tryUseStoredAccount();
      }

      // This will check if the app was refreshed and clear cache if needed
      this.cacheManagerService.checkAndHandleAppRefresh();

      // Add a safety timeout to reset interaction state if stuck
      setTimeout(() => {
        if (this.authService.isInteractionInProgress()) {
          console.log('Authentication appears to be stuck, resetting state...');
          this.authService.forceResetInteractionState();
          
          // Navigate to login page
          this.router.navigate(['/auth/login']);
        }
      }, 10000); // 10 seconds timeout
    } catch (error) {
      console.error('Error during app initialization:', error);
      this.isInitializing = false;
      
      // Reset auth state and navigate to login
      this.authService.forceResetInteractionState();
      this.router.navigate(['/auth/login']);
    }
  }

  private retryInitialization() {
    if (this.retryCount < this.maxRetries) {
      this.retryCount++;
      console.log(`Retrying MSAL initialization (attempt ${this.retryCount})`);
      
      setTimeout(() => {
        this.authService.ensureMsalInitialized().then(() => {
          if (this.authService.isAuthenticated()) {
            console.log('Authentication successful after retry');
          } else {
            console.log('Still not authenticated after retry');
            this.authService.tryUseStoredAccount();
          }
        }).catch(error => {
          console.error('Retry initialization failed:', error);
          this.retryInitialization();
        });
      }, 1000 * this.retryCount); // Increasing delay for each retry
    } else {
      console.error('Max retries reached. Please refresh the page.');
    }
  }

  public resetAuthState(): void {
    this.authService.forceResetInteractionState();
    // Try to navigate to a safe location
    this.router.navigate(['/auth/login']);
  }

  public reloadPage(): void {
    // Clear storage first
    this.authService.clearMsalStorage();
    
    // Wait a moment and reload
    setTimeout(() => {
      window.location.reload();
    }, 500);
  }
}
